import { useState, useCallback } from "react";

const MAX_INTERMEDIATES = 23;

export const createEmptyPoint = (order) => ({
  id: `point_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  order,
  type: "address",
  label: "",
  location: "",
  latitude: null,
  longitude: null,
  visitPointId: null,
});

const isPointValid = (point) => {
  if (!point) return false;
  if (point.type === "coordinate") {
    return point.latitude != null && point.longitude != null && !!point.location;
  }
  return !!point.location;
};


export const toLocationString = (point) => {
  if (!point) return "";
  if (point.latitude != null && point.longitude != null) {
    return `${point.latitude},${point.longitude}`;
  }
  return point.location || "";
};

export function useRoutePoints() {
  const [origin, setOriginState] = useState(createEmptyPoint(1));
  const [destination, setDestinationState] = useState(createEmptyPoint(2));
  const [points, setPoints] = useState([]);

  // Merge parcial — preserva id e order, só atualiza os campos que o card manda
  const setOrigin = useCallback((partialData) => {
    setOriginState((prev) => ({ ...prev, ...partialData }));
  }, []);

  const setDestination = useCallback((partialData) => {
    setDestinationState((prev) => ({ ...prev, ...partialData }));
  }, []);

  const addPoint = useCallback((pointData) => {
    setPoints((prev) => {
      if (prev.length >= MAX_INTERMEDIATES) return prev;
      const nextOrder = prev.length + 3;
      return [
        ...prev,
        {
          ...createEmptyPoint(nextOrder),
          ...pointData,
          order: nextOrder,
        },
      ];
    });
  }, []);

  const updatePoint = useCallback((id, pointData) => {
    setPoints((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...pointData } : p))
    );
  }, []);

  const removePoint = useCallback((id) => {
    setPoints((prev) => {
      const filtered = prev.filter((p) => p.id !== id);
      return filtered.map((p, idx) => ({ ...p, order: idx + 3 }));
    });
  }, []);

  const reorderPoints = useCallback((fromIndex, toIndex) => {
    setPoints((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);

      return updated.map((p, idx) => ({
        ...p,
        order: idx + 3,
      }));
    });
  }, []);

  const clearAll = useCallback(() => {
    setOriginState(createEmptyPoint(1));
    setDestinationState(createEmptyPoint(2));
    setPoints([]);
  }, []);

  const canCalculateRoute =
    isPointValid(origin) && isPointValid(destination);

  const buildRoutingPayload = useCallback(
    (vehicleType = "Drive") => ({
      origins: [{ location: toLocationString(origin) }],
      destinations: [{ location: toLocationString(destination) }],
      intermediates: points
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((p) => ({ location: toLocationString(p) })),
      optimized: points.length > 0,
      VehicleType: vehicleType,
      vehicleType,
    }),
    [origin, destination, points]
  );

  return {
    origin,
    setOrigin,
    destination,
    setDestination,
    points,
    addPoint,
    updatePoint,
    removePoint,
    reorderPoints,
    clearAll,
    canCalculateRoute,
    buildRoutingPayload,
    maxIntermediates: MAX_INTERMEDIATES,
    isAtMaxIntermediates: points.length >= MAX_INTERMEDIATES,
  };
}
